import React from 'react';
import PropTypes from 'prop-types';
import {handleEvent} from '../../../../events/event-handler';
import CaptionsNodeContainer from './captions-node';
import {filterNodeProperties} from '../../../../utilities/filter-node-properties';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';


const getDataFromNode = function (node, requireAutoPlay = false) {
    const keys = ['id', 'x', 'y', 'height', 'width', 'src', 'showcontrols', 'loop', 'start', 'visible'];
    const nodeProps = filterNodeProperties(node, keys);
    nodeProps.start = nodeProps.start === '1' || requireAutoPlay ;
    nodeProps.loop = nodeProps.loop === '1';
    return nodeProps;
};

const getStateFromProps = function (props = {}) {
    const nodeData = getDataFromNode(props.node, props.requireAutoPlay);

    return {
        ...nodeData,
        startCaption: !!nodeData.start,
    };
};

class VideoNode extends React.Component {
    constructor(props) {
        super(props);

        // requireAutoPlay is passed to a slide-node's VideoNode. This sets the nodeData's start attribute to true.
        // Without it, we would need to add a transformer fix to every slide-node's video in the JRE
        const nodeData = getDataFromNode(props.node, props.requireAutoPlay);

        this.state = {
            ...nodeData,
            startCaption: !!nodeData.start,
            captionsNode: null,
            zIndex: props.zIndex,
            visible: (nodeData.visible) ? nodeData.visible === '1' : true, //Use the initial visible attribute if it exists (GRAV 3.2.0 p114/127)
            preventRepeat: false, // true after a video is played, so that we know not to replay the old video upon playing another audio/video.
        };
        this.maxPlayed = 0;  // video-player forward seeking prevention
        this.hideVideo = this.hideVideo.bind(this);
        this.showVideo = this.showVideo.bind(this);
        this.videoStart = this.videoStart.bind(this);
        this.videoStop = this.videoStop.bind(this);
        this.handleTimeUpdate = this.handleTimeUpdate.bind(this);// video-player forward seeking prevention
        this.handleSeeking = this.handleSeeking.bind(this);// video-player forward seeking prevention
    }

    componentDidMount() {
        const {disableJREAppPlayerContainerButtons} = this.props;
        const {loop} =this.state;
        if (!loop){
            disableJREAppPlayerContainerButtons('preventMultiClick', 'VideoNode componentDidMount');
        }

        document.addEventListener('jre-video-hide', this.hideVideo);
        document.addEventListener('jre-video-show', this.showVideo);
        document.addEventListener('jre-video-start', this.videoStart);
        document.addEventListener('jre-video-stop', this.videoStop);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.node === nextProps.node) {
            this.setState({
                ...getStateFromProps(nextProps)
            });
        }
        // this check ensures that no race condition between 2 video's play button occurs
        // In other words, without this check, a user can quickly pause a previous slide's video from the next slide
        if (nextProps.pauseFlag) { // pause/play the video when the global pause/play toggle is clicked
            this.video.pause();
        } else if (!this.state.preventRepeat && nextProps.pauseFlag === false) {
            // When switching to a new video page, checking pauseFlag to be false prevents the old video from replaying for a moment.
            // If there are multiple videos on the same page, preventRepeat prevents the original video from replaying unexpectedly.
            this.video.play();
        }
    }

     // video-player forward seeking prevention
    componentDidUpdate(prevProps, prevState) {
        if (prevState.src !== this.state.src) {
            this.maxPlayed = 0;
        }
    }

    componentWillUnmount() {
        document.removeEventListener('jre-video-hide', this.hideVideo);
        document.removeEventListener('jre-video-show', this.showVideo);
        document.removeEventListener('jre-video-start', this.videoStart);
        document.removeEventListener('jre-video-stop', this.videoStop);
    }

    hideVideo(e) {
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            this.setState({visible: false});
        }
    }

    showVideo(e) {
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            this.setState({visible: true});
        }
    }

    videoEnded() {
        const event_nodes = this.props.node.childrenNamed('event_node');
        event_nodes.forEach(function (event) {
            if (event.attr.name === 'onvideocomplete') {
                handleEvent(event);
            }
        });

        this.setState({preventRepeat: true}, () => {
            const { enableJREAppPlayerContainerButtons} = this.props;
            enableJREAppPlayerContainerButtons('preventMultiClick', 'VideoNode videoEnded');

            document.dispatchEvent(new Event('jre-video-ended'));
        });
    }

    //video-player forward seeking prevention
    handleTimeUpdate() {
        const video = this.video;
        if (!video || video.seeking || video.currentTime <= this.maxPlayed) {
            return;
        }
        this.maxPlayed = video.currentTime;
    }

    handleSeeking() {
        const video = this.video;
        if (!video || this.fixingSeek || !window.jreRestrictForwardSeek || video.currentTime <= this.maxPlayed) {
            return;
        }
        this.fixingSeek = true;
        video.currentTime = this.maxPlayed;
        setTimeout(() => {
            this.fixingSeek = false;
        }, 0);
    }

    // runs when an event triggers video start
    videoStart(e){
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            this.video.play();
        }
    }

    // runs when an event triggers video stop
    videoStop(e){
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            this.video.pause();
        }
    }

    // runs from synthetic events (clicking play on video, when video autoplays)
    videoPlay() {
        this.setState({
            startCaption: true,
            captionsNode: this.props.node.childNamed('captions_node') || null
        });
        document.dispatchEvent(new Event('jre-video-play'));
    }

    // runs from synthetic events (clicking pause on video)
    videoPause() {
        document.dispatchEvent(new Event('jre-video-pause'));
    }

    render() {
        const {moduleURL = ''} = this.props;
        const {x = 0, y = 0, height = 0, width = 0, src = '', loop = false, start = false, visible = false,
            startCaption = true, captionsNode = null, zIndex = null} = this.state;
        const divStyle = {
            top: `${y}px`,
            left: `${x}px`,
            display: visible ? 'block' : 'none'
        };
        let fullHeightVideo = false;
        if(height > 710) {
            fullHeightVideo = true;
        }

        const videoContainerStyle = {
            width: `${width}px`
        };

        if (zIndex !== null) {
            divStyle.zIndex = zIndex;
        }
        else {
            /* With this z-index setting we want to make sure that images do not overlap the video in such a way that
            the user cannot access the the video's controls. Last value we had here was 2. */
            divStyle.zIndex = 3;
        }
        return (
            <div className="node" style={divStyle}>
                <div style={videoContainerStyle}>
                    {/* We want controls enabled even though the XML file always sets showcontrols=0. */}
                    <video className="video-node" ref={(video) => { this.video=video; }} loop={loop} autoPlay={start === true}
                           width={width} height={height} onPlay={this.videoPlay.bind(this)} onPause={this.videoPause.bind(this)}
                           onEnded={this.videoEnded.bind(this)} 
                        //    onTimeUpdate={this.handleTimeUpdate} onSeeking={this.handleSeeking}
                           controlsList="nodownload" controls>
                        Your browser doesn't support HTML5 video.
                        <source src={`${moduleURL}/media/${src}`} type="video/mp4" />
                    </video>

                    {(startCaption && captionsNode) &&
                    <CaptionsNodeContainer fullHeightVideo={fullHeightVideo} pause={this.props.pauseFlag} type="video" key={captionsNode.attr.id} node={captionsNode} />}
                </div>
            </div>
        );
    }
}
const mapStateToProps = (state) => {
    return {
        pauseFlag: state.pauseFlag,
    };
};

VideoNode.propTypes = {
    node: PropTypes.object.isRequired,
    zIndex: PropTypes.number,
    requireAutoPlay: PropTypes.bool,
};

const VideoNodeContainer = connect(
    mapStateToProps, null
)(VideoNode);

export default withRouter(VideoNodeContainer);



// WEBPACK FOOTER //
// ./src/js/components/app/jre/elements/video-node.js